import Vue from 'vue';
import createAuth0Client, { Auth0Client } from '@auth0/auth0-spa-js';
import { Plugin, Context } from '@nuxt/types';
import { getAccessToken } from '../lib/getAccessToken';
import jwtDecode from 'jwt-decode';

let instance: Vue;
let auth0Client: Auth0Client;

const redirectUri = window.location.origin;

/** Returns the current instance of the SDK */
export const getInstance = (): any => instance;

async function tryCreateAuth0Client(context) {
  try {
    // Create a new instance of the SDK client using members of the given options object
    return await createAuth0Client({
      domain: process.env.Auth0Domain,
      client_id: process.env.Auth0ClientId,
      audience: process.env.Auth0Audience,
      redirect_uri: redirectUri,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      environment: process.env.Env
    });
  } catch (e) {
    const auth0Key = Object.keys(localStorage).find(x =>
      x.includes('@@auth0spajs@@')
    );
    const expiryTime = auth0Key
      ? JSON.parse(localStorage.getItem(auth0Key)).expiresAt
      : 0;

    context.$appInsights.trackException({
      exception: e,
      properties: {
        exceptionType: 'failureToCreateAuth0Client',
        hasAuthToken: !!auth0Key,
        authTokenExpiryTime: expiryTime
      }
    });
    if (!auth0Key) return;
    // If client can't be created delete exisiting auth0Key and try again.
    // This will force reauthentication for the client
    localStorage.removeItem(auth0Key);
    location.reload();
  }
}

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useAuth0 = context => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: auth0Client,
        popupOpen: false,
        error: null,
        accessToken: null
      };
    },
    computed: {
      decodedAccessToken(): any {
        return jwtDecode(this.accessToken || '');
      },
      permissions(): string[] {
        return this.decodedAccessToken?.permissions || [];
      },
      isAdmin(): boolean {
        return this.permissions.includes('admin');
      }
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(o) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(o);
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        } finally {
          this.popupOpen = false;
        }

        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
        this.accessToken = getAccessToken();
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.accessToken = getAccessToken();
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      /** Gets the access token using a popup window */
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      }
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await tryCreateAuth0Client(context);

      try {
        // The presence of these query parameters indicate that the user has been
        // redirected back to the callback page (the app) after authenticating on
        // Auth0.
        if (
          window.location.search.includes('code=') &&
          window.location.search.includes('state=')
        ) {
          // Handle success and error responses from Auth0.
          await this.auth0Client.handleRedirectCallback();
        }
      } catch (e) {
        this.error = e;
      } finally {
        // Initialize Auth0 internal authentication state.
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.user = await this.auth0Client.getUser();
        this.accessToken = getAccessToken();
        this.loading = false;
      }
    }
  });

  return instance;
};

const authWrapper: Plugin = async (context: Context, inject) => {
  inject('auth', useAuth0(context));
};

export default authWrapper;
